import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Button,
  Chip,
  InputAdornment,
  TextField,
  ThemeProvider,
} from "@mui/material";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import InboxIcon from "@mui/icons-material/Inbox";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { selectUser } from "../../features/userSlice";
import { selectRoles } from "../../features/roleSlice";
import SubmissionDashboard from "../../components/SBCF/SpecSheet/SubmissionDashboard";
import AssignDialog from "../../components/SBCF/SpecSheet/AssignDialog";
import TeamWorkload from "../../components/SBCF/SpecSheet/TeamWorkload";
import { fetchSubmissions } from "../../components/SBCF/SpecSheet/api";
import { formatSpecDate } from "../../components/SBCF/SpecSheet/specSheetConfig";
import { useAppMuiTheme } from "../../theme/MuiThemeBridge";
import { SectionHeader } from "../../components/UI/Page";
import { EmptyState, LoadingState } from "../../components/UI/States";
import { Notice, Segmented } from "../../components/SBCF/ui";

export const ASSIGNER_ROLES = ["Admin", "SBCF Assigner"];
export const SBCF_QUEUE_ROLES = ["Admin", "SBCF Assigner", "SBCF Team"];

const STATUS_FILTERS = ["All", "New", "Assigned", "In Progress", "Complete"];

const STATUS_COLORS = {
  New: "default",
  Assigned: "warning",
  "In Progress": "info",
  Complete: "success",
};

// mode "manage": the SBCF work queue (assigners see everything, team members
// see the forms assigned to them). mode "sales": the submitter's own forms.
const SpecSheetQueue = ({ mode }) => {
  const user = useSelector(selectUser);
  const role = useSelector(selectRoles);
  const isAssigner = ASSIGNER_ROLES.some((r) => role?.roles?.includes(r));

  const fetchMode = mode === "sales" ? "sales" : isAssigner ? "assigner" : "team";
  const canAssign = mode === "manage" && isAssigner;
  const showInternal = mode === "manage";

  // Email links deep-link straight to a form via ?submission=<id>
  const [searchParams] = useSearchParams();

  const [submissions, setSubmissions] = useState(null);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(
    searchParams.get("submission") || null
  );
  const [assignTarget, setAssignTarget] = useState(null);
  const [subView, setSubView] = useState("queue"); // "queue" | "workload" (assigners only)

  // The search field and the outlined status/product Chips are MUI, which
  // keeps its own palette — left on the light default their text renders
  // near-black on the dark card.
  const muiTheme = useAppMuiTheme();

  const load = () => {
    fetchSubmissions(fetchMode, user?.id)
      .then((list) => {
        setSubmissions(list);
        setError(null);
      })
      .catch((err) => setError(err.message));
  };

  useEffect(() => {
    if (user?.id || fetchMode === "assigner") load();
  }, [fetchMode, user?.id]);

  if (selectedId) {
    return (
      <ThemeProvider theme={muiTheme}>
        <SubmissionDashboard
          submissionId={selectedId}
          user={user}
          canAssign={canAssign}
          showInternal={showInternal}
          onBack={() => setSelectedId(null)}
          onChanged={load}
        />
      </ThemeProvider>
    );
  }

  const filtered = (submissions || []).filter((s) => {
    if (statusFilter !== "All" && s.status !== statusFilter) return false;
    if (search.trim()) {
      const haystack = [
        s.values?.contractorName,
        s.values?.address,
        s.values?.state,
        s.submitted_by?.name,
        ...(s.assigned_to || []).map((a) => a.name),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(search.trim().toLowerCase())) return false;
    }
    return true;
  });

  return (
    <ThemeProvider theme={muiTheme}>
    <div className="space-y-4">
      <SectionHeader
        className="!mb-1"
        title={
          mode === "sales"
            ? "My Submissions"
            : subView === "workload"
            ? "Team Workload"
            : "Spec Sheet Queue"
        }
        description={
          mode === "sales"
            ? "Spec sheets you've submitted — open one to check status and message the SBCF team."
            : subView === "workload"
            ? "Who's working on what — assign, unassign, and spot free capacity."
            : isAssigner
            ? "All submitted spec sheets. Assign them to team members and track progress."
            : "Spec sheets assigned to you."
        }
        actions={
          canAssign ? (
            <Segmented
              ariaLabel="Queue view"
              value={subView}
              onChange={setSubView}
              options={[
                { value: "queue", label: "Queue" },
                { value: "workload", label: "Team Workload" },
              ]}
            />
          ) : null
        }
      />

      {error && (
        <Notice tone="rose" className="font-medium">
          {error}
        </Notice>
      )}

      {canAssign && subView === "workload" ? (
        <TeamWorkload
          submissions={submissions || []}
          onOpen={setSelectedId}
          onChanged={load}
        />
      ) : (
        <>
      {/* Filters */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <Segmented
          ariaLabel="Filter by status"
          value={statusFilter}
          onChange={setStatusFilter}
          options={STATUS_FILTERS.map((s) => ({ value: s, label: s }))}
        />
        <TextField
          size="small"
          placeholder="Search contractor, address, name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="md:w-80"
          inputProps={{ "aria-label": "Search spec sheets" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </div>

      {/* Table */}
      <div className="app-card overflow-hidden rounded-2xl border">
        <div className="overflow-x-auto">
          <table className="app-table">
            <thead>
              <tr>
                <th>Submitted</th>
                <th>Contractor</th>
                <th>Address</th>
                <th>Products</th>
                <th>Needed</th>
                <th>Status</th>
                {mode === "manage" && <th>Assigned To</th>}
                <th aria-label="Open" />
              </tr>
            </thead>
            <tbody>
              {submissions === null ? (
                <tr>
                  <td colSpan={8}>
                    <LoadingState label="Loading submissions..." />
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    {submissions.length === 0 ? (
                      <EmptyState
                        icon={InboxIcon}
                        title={
                          mode === "sales"
                            ? "You haven't submitted any spec sheets yet."
                            : "No spec sheets here yet."
                        }
                        description={
                          mode === "sales"
                            ? "Spec sheets you send from the Blueprint Spec Sheet tab show up here."
                            : isAssigner
                            ? "New spec sheets show up here as soon as sales submits them."
                            : "Spec sheets assigned to you show up here."
                        }
                      />
                    ) : (
                      <EmptyState
                        icon={SearchOffIcon}
                        title="Nothing matches the current filters."
                        description="Try another status or clear the search."
                      />
                    )}
                  </td>
                </tr>
              ) : (
                filtered.map((s) => (
                  <tr
                    key={s._id}
                    onClick={() => setSelectedId(s._id)}
                    className="cursor-pointer"
                  >
                    <td className="whitespace-nowrap">
                      <div className="tabular-nums">
                        {new Date(s.submitted_at).toLocaleDateString()}
                      </div>
                      <div className="app-muted text-xs">
                        {s.submitted_by?.name}
                      </div>
                    </td>
                    <td className="font-medium">
                      {s.values?.contractorName || "—"}
                    </td>
                    <td className="max-w-[220px] truncate">
                      {s.values?.address ||
                        (s.values?.latLng ? "Pinned location" : "—")}
                    </td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {(s.products || []).map((p) => (
                          <Chip key={p} label={p} size="small" variant="outlined" />
                        ))}
                      </div>
                    </td>
                    <td className="whitespace-nowrap tabular-nums">
                      {s.values?.requiredDate === "ASAP" ? (
                        <Chip label="ASAP" color="error" size="small" />
                      ) : (
                        formatSpecDate(s.values?.requiredDate) || "—"
                      )}
                    </td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        <Chip
                          label={s.status}
                          color={STATUS_COLORS[s.status] || "default"}
                          size="small"
                        />
                        {s.approval?.state === "pending" && (
                          <Chip
                            label="Awaiting Approval"
                            color="secondary"
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {s.approval?.state === "changes_requested" && (
                          <Chip
                            label="Changes Requested"
                            color="error"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </div>
                    </td>
                    {mode === "manage" && (
                      <td>
                        <div className="flex flex-wrap items-center gap-1">
                          {(s.assigned_to || []).map((a) => (
                            <Chip key={a.id} label={a.name} size="small" />
                          ))}
                          {canAssign && (
                            <Button
                              size="small"
                              startIcon={<GroupAddIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                setAssignTarget(s);
                              }}
                            >
                              {(s.assigned_to || []).length ? "Edit" : "Assign"}
                            </Button>
                          )}
                        </div>
                      </td>
                    )}
                    <td className="text-right">
                      <ChevronRightIcon className="app-muted" fontSize="small" />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AssignDialog
        open={!!assignTarget}
        submission={assignTarget}
        onClose={() => setAssignTarget(null)}
        onSaved={() => load()}
      />
        </>
      )}
    </div>
    </ThemeProvider>
  );
};

export default SpecSheetQueue;
